import { useEffect, useState } from "react";
import { AlertDialog, Text } from "dssp-ui-lib";
import { UseJsxT, useStringT } from "@/functions/i18n/LanguageFormatter";
import { useTranslation } from "react-i18next";
import { triggerAnalytics } from "./analyticsSession";

interface SessionTimeoutDialogProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    initialSeconds: number;
    onContinue: () => void;
    onTimeout: () => void;
}

export function SessionTimeoutDialog({
    open,
    setOpen,
    initialSeconds,
    onContinue,
    onTimeout,
}: SessionTimeoutDialogProps) {
    const { i18n } = useTranslation("stitcher");
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

    const title = useStringT("ui.session.timeout.title");
    const continueButton = useStringT("ui.session.timeout.continue");

    useEffect(() => {
        if (!open) {
            return;
        }

        triggerAnalytics("Session expiring", i18n.language)

        const expiresAt = Date.now() + initialSeconds * 1000;

        let timer: number | undefined;
        let hasLoggedOut = false;

        const updateRemainingSeconds = () => {
            const remainingMilliseconds = expiresAt - Date.now();

            if (remainingMilliseconds <= 0) {
                setRemainingSeconds(0);

                if (!hasLoggedOut) {
                    hasLoggedOut = true;

                    if (timer !== undefined) {
                        window.clearInterval(timer);
                    }

                    onTimeout();
                }

                return;
            }

            // Keep 1 second displayed until the actual expiry time.
            const remainingSeconds = Math.ceil(remainingMilliseconds / 1000);

            setRemainingSeconds(remainingSeconds);
        };

        updateRemainingSeconds();

        timer = window.setInterval(updateRemainingSeconds, 1000);

        return () => {
            if (timer !== undefined) {
                window.clearInterval(timer);
            }
        };
    }, [open, initialSeconds, onTimeout]);

    // useEffect(() => {
    //     if (open && remainingSeconds !== null && remainingSeconds <= 0) {
    //         onTimeout();
    //     }
    // }, [open, remainingSeconds, onTimeout]);

    const formatTime = (seconds: number): string => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        return `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    return (
        <AlertDialog
            iconType={"reject"}
            isOpen={open}
            setOpen={setOpen}
            onClick={onContinue}
            width={"min(500px, calc(100vw - 32px))"}
            title={title}
            btnLabel={continueButton}
            isHTMLContent
            htmlMessage={
                <>
                    <Text
                        size={"sm"}
                        className={
                            "rds-text-[14px] rds-leading-[22px] md:rds-text-[16px] md:rds-leading-[26px] rds-text-neutral-100 rds-mb-6 rds-text-center"
                        }
                    >
                        <UseJsxT k={"ui.session.timeout.message"} />
                        <br />
                        <b>{formatTime(remainingSeconds)}</b>.
                        <br />
                    </Text>
                    <Text
                        size={"sm"}
                        className={
                            "rds-text-[14px] rds-leading-[22px] md:rds-text-[16px] md:rds-leading-[24px] rds-text-neutral-100 rds-text-center"
                        }
                    >
                        <UseJsxT k={"ui.session.timeout.message2"} />
                    </Text>
                </>
            }
        />
    );
}
